/**
 * Reading the `GET /v1/products/check-sale` response.
 *
 * The endpoint prices the cart for the "1+1=3" promo (every third cheapest unit
 * costs 1 грн). The request format is unchanged — `products[<product_price_id>]=<count>`,
 * exactly what the cart already sends alongside `check-gift` / `check-samples`.
 *
 * Field names here are camelCase because `src/utils/https.js` runs
 * `camelcaseObjectDeep` over every response; the backend spec writes them in
 * snake_case (`unit_price`, `free_count`, `in_third_free`).
 */

/**
 * The price row of a `check-sale` entry.
 *
 * `data[]` is keyed by **cart line, not by product**: two volumes of one product
 * come back as two entries carrying the same `entry.id`, each with a single
 * price row. So the map below is keyed by `product_price_id` — keying by product
 * would collapse the two volumes into one.
 */
const entryPriceRow = entry => {
  const prices = entry && entry.productPrices && entry.productPrices.data;

  return prices && prices[0] ? prices[0] : null;
};

/**
 * @param {any} payload the whole `response.data`, i.e. `{ data, promo }`
 * @returns {Object.<string, any>} rows by `product_price_id`
 */
export const saleRowsFrom = payload => {
  /** @type {Object.<string, any>} */
  const rows = {};

  ((payload && payload.data) || []).forEach(entry => {
    const price = entryPriceRow(entry);
    if (!price || price.id == null) return;

    rows[price.id] = {
      productPriceId: price.id,
      productId: entry.id,
      count: Number(price.count) || 0,
      unitPrice: Number(price.unitPrice) || 0,
      sum: Number(price.sum) || 0,
      freeCount: Number(price.freeCount) || 0,
      inThirdFree: !!price.inThirdFree,
      appliedDiscount: Number(price.appliedDiscount) || 0,
      oldPrice: price.oldPrice == null ? null : Number(price.oldPrice),
      // The row total without 1+1=3. Sent only on rows the promo touched, so
      // null is meaningful — it is not the same as "no saving".
      oldSum: price.oldSum == null ? null : Number(price.oldSum),
      price: Number(price.price) || 0,
      basePrice: Number(price.basePrice) || 0,
    };
  });

  return rows;
};

/** @returns {any} the promo counters, or null when the response carries none */
export const salePromoFrom = payload => {
  const promo = payload && payload.promo;
  if (!promo) return null;

  return {
    active: !!promo.active,
    freeUnits: Number(promo.freeUnits) || 0,
    unitsToNext: Number(promo.unitsToNext) || 0,
    unitsInBasket: Number(promo.unitsInBasket) || 0,
    discount: Number(promo.discount) || 0,
  };
};

/**
 * Whether a row's discount is worth drawing.
 *
 * Six campaigns with a zero percentage (e.g. «• Подарунок (за умовою)») come back
 * as `appliedDiscount: 0` with `oldPrice === price` — drawing that gives a
 * struck-through price identical to the current one and a «−0%» badge. Promo
 * rows are excluded for a different reason: 1+1=3 cancels the percentage
 * discount on its rows, so they arrive without an `oldPrice` at all.
 */
export const rowHasVisibleDiscount = row =>
  !!row && row.appliedDiscount > 0 && !!row.oldPrice && row.oldPrice !== row.price;
